import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import { StatusTextBadge as Badge } from "../../../components/base/StatusTextBadge";
import { OriginCell } from "../components/ImportedFromTag";
import { boardScrollbar } from "./boardScroll";
import { AttachmentsList } from "../components/AttachmentsList";
import { fetchAttachmentUrl } from "../services/attachmentsService";
import {
  BUCKET_META,
  BUCKET_WEIGHT,
  EXPIRY_LABEL,
  PERIOD_LABEL,
  amountOf,
  bucketOf,
  daysLeft,
  effectiveStatus,
  filterQuotes,
  isItemized,
  isMirrorQuote,
  periodOf,
  quoteKpis,
  stagesOf,
  validUntilMs,
  type ExpiryFilter,
  type PeriodPreset,
  type Quote,
  type QuoteBucket,
} from "../services/quotesAnalytics";
import {
  fetchQuoteDocumentUrl,
  fetchQuotes,
  removeQuoteDocument,
  updateQuote,
  uploadQuoteDocument,
} from "../services/quotesService";
import { newQuotePath, quoteEditorPath } from "../services/quotePaths";
import { useSafeNavigate } from "../hooks/useSafeNavigate";

// ===========================================================================
// Cotizaciones — seguimiento. KPIs arriba (valor en negociación ponderado por
// temperatura, congelado, perdido, cotizaciones por mes y valor promedio),
// filtros por fecha de envío, etapa del negocio y vencimiento, y la lista con
// importe, «Válida hasta» y documento editables. Las reglas viven en
// services/quotesAnalytics.
//
// Armador (#1545): «Nueva cotización» y las que tienen renglones (armadas en la
// consola o pedidas en la tienda) se abren en el armador
// (/crm/negocios/negocios/cotizaciones/:id). Los borradores —p. ej. las
// solicitudes que llegan de la tienda— se listan arriba en «Por enviar», sin
// filtro de fecha (todavía no tienen fecha de envío).
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;

const money = (value: number, currency = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);

const fmtDate = (t: number | null) =>
  t === null ? "—" : new Date(t).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });

/** `YYYY-MM-DD` of a ms timestamp in local time, for date inputs. */
const toInputDate = (t: number | null) => {
  if (t === null) return "";
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const errText = (e: unknown, fallback: string) => {
  const data = (e as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data;
  return data?.meta?.message || data?.message || (e instanceof Error ? e.message : fallback);
};

const STATUS_OPTIONS = ["borrador", "enviada", "aceptada", "rechazada", "cancelada"];
const PERIODS: PeriodPreset[] = ["this_month", "last_month", "last_3_months", "this_year", "all", "custom"];
const OPEN_BUCKETS: QuoteBucket[] = ["caliente", "tibia", "fria"];

/** Origen: «Tienda · folio» para lo que llega de la tienda; «Consola» para lo armado aquí. */
function QuoteOrigin({ quote }: { quote: Quote }) {
  if (quote.source === "store") {
    return (
      <span data-testid={`quote-origin-store-${quote.id}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-500/15 px-2 py-0.5 text-[11px] font-semibold text-sky-600">
        <i className="ri-store-2-line" /> Tienda{quote.folio ? ` · ${quote.folio}` : ""}
      </span>
    );
  }
  if (quote.source === "manual") {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500/15 px-2 py-0.5 text-[11px] font-semibold text-primary-500">
        <i className="ri-draft-line" /> Consola{quote.folio ? ` · ${quote.folio}` : ""}
      </span>
    );
  }
  return <OriginCell source={quote.source} />;
}

function Kpi({ tc, label, value, hint, icon, accent }: { tc: TC; label: string; value: string; hint?: string; icon: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className={`text-xs ${tc.textSecondary}`}>{label}</span>
        <i className={`${icon} ${accent ? "text-primary-500" : tc.textSecondary}`} />
      </div>
      <div className={`text-lg font-bold ${accent ? "text-primary-500" : tc.text}`}>{value}</div>
      {hint && <div className={`text-[11px] ${tc.textSecondary}`}>{hint}</div>}
    </div>
  );
}

export default function QuotesSection() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [preset, setPreset] = useState<PeriodPreset>("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [stage, setStage] = useState("");
  const [expiry, setExpiry] = useState<ExpiryFilter>("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Quote | null>(null);
  const navigate = useSafeNavigate();
  /** Las que tienen renglones van al armador; las de seguimiento, al editor rápido. */
  const openQuote = (quote: Quote) => (isItemized(quote) && !isMirrorQuote(quote) ? navigate(quoteEditorPath(quote.id)) : setEditing(quote));

  // One clock for every cell and KPI; it moves forward when the data is reloaded.
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setQuotes(await fetchQuotes());
      setNow(Date.now());
    } catch (e) {
      setError(errText(e, "No se pudieron cargar las cotizaciones"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const period = useMemo(() => periodOf(preset, now, customFrom, customTo), [preset, now, customFrom, customTo]);
  const filtered = useMemo(
    () => filterQuotes(quotes, { period, stage, expiry, query }, now).sort((a, b) => (Date.parse(b.sentAt ?? "") || 0) - (Date.parse(a.sentAt ?? "") || 0)),
    [quotes, period, stage, expiry, query, now],
  );
  const kpis = useMemo(() => quoteKpis(filtered, period, now), [filtered, period, now]);
  const stages = useMemo(() => stagesOf(quotes), [quotes]);
  const drafts = useMemo(
    () =>
      quotes
        .filter((q) => String(q.status ?? "borrador") === "borrador" && !isMirrorQuote(q))
        .sort((a, b) => String(b.requestedAt ?? b.id).localeCompare(String(a.requestedAt ?? a.id))),
    [quotes],
  );

  const fieldCls = isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900";
  const chip = (active: boolean) =>
    `rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? "border-primary-500 bg-primary-500 text-white"
        : isDarkMode
          ? "border-gray-500 bg-dark-card text-white hover:bg-white/10"
          : "border-gray-300 bg-white text-gray-900 hover:bg-gray-100"
    }`;

  const openDocument = async (quote: Quote) => {
    if (quote.document) {
      try {
        window.open(await fetchQuoteDocumentUrl(quote.id), "_blank", "noopener,noreferrer");
      } catch (e) {
        Swal.fire({ icon: "error", title: "No se pudo abrir el documento", text: errText(e, "Intenta de nuevo"), confirmButtonColor: brand.primary });
      }
    } else if (quote.attachmentId) {
      try {
        window.open(await fetchAttachmentUrl(quote.attachmentId), "_blank", "noopener,noreferrer");
      } catch (e) {
        Swal.fire({ icon: "error", title: "No se pudo abrir el documento", text: errText(e, "Intenta de nuevo"), confirmButtonColor: brand.primary });
      }
    } else if (quote.documentUrl) {
      window.open(quote.documentUrl, "_blank", "noopener,noreferrer");
    }
  };

  const patchLocal = (id: string, patch: Partial<Quote>) => setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));

  return (
    <div data-testid="quotes-section" className={`flex h-full min-h-0 w-full min-w-0 flex-col overflow-y-auto p-6 ${boardScrollbar(isDarkMode)}`}>
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-file-list-2-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>Cotizaciones</h1>
            <p className={`text-sm ${tc.textSecondary}`}>Propuestas enviadas: cuánto hay en negociación, qué tan viva está cada una y qué se perdió.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
            <i className={`ri-search-line ${tc.textSecondary}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar cotización, empresa o contacto"
              className={`w-60 bg-transparent text-sm outline-none ${tc.text}`}
            />
          </div>
          <button onClick={load} title="Actualizar" className={`rounded-lg border px-3 py-2 text-sm ${fieldCls}`}>
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => navigate(newQuotePath())} className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-400">
            <i className="ri-add-line" /> Nueva cotización
          </button>
        </div>
      </div>

      {/* Por enviar: borradores (las solicitudes de la tienda llegan aquí). */}
      {!loading && !error && drafts.length > 0 && (
        <div className={`mb-4 rounded-xl border ${tc.cardBorder} ${tc.cardBg}`} data-testid="quote-drafts">
          <div className={`flex items-center justify-between border-b px-4 py-2 ${tc.border}`}>
            <h2 className={`text-sm font-bold ${tc.text}`}>
              <i className="ri-draft-line mr-1 text-primary-500" /> Por enviar ({drafts.length})
            </h2>
            <span className={`text-xs ${tc.textSecondary}`}>Borradores y solicitudes de la tienda: ponles precio y envíalas.</span>
          </div>
          <ul className={`divide-y ${tc.border}`}>
            {drafts.slice(0, 20).map((q) => (
              <li key={q.id} data-testid={`quote-draft-${q.id}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                <div className="min-w-0">
                  <p className={`truncate text-sm font-medium ${tc.text}`}>{q.title || q.folio || "Cotización"}</p>
                  <p className={`truncate text-xs ${tc.textSecondary}`}>
                    {[q.company || q.customer?.company, q.contact || q.customer?.name, q.product].filter(Boolean).join(" · ") || "Sin cliente"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <QuoteOrigin quote={q} />
                  {q.hasUnpriced && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-600">Por cotizar</span>}
                  <span className={`text-sm font-semibold ${tc.text}`}>{money(amountOf(q), q.currency || "MXN")}</span>
                  <button onClick={() => openQuote(q)} className="rounded-lg bg-primary-500 px-3 py-1 text-xs font-medium text-white hover:bg-primary-400">
                    Abrir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Enviada">
          <span className={`w-20 text-xs ${tc.textSecondary}`}>
            <i className="ri-calendar-line mr-1" />
            Enviada
          </span>
          {PERIODS.map((p) => (
            <button key={p} className={chip(preset === p)} aria-pressed={preset === p} onClick={() => setPreset(p)}>
              {PERIOD_LABEL[p]}
            </button>
          ))}
          {preset === "custom" && (
            <>
              <input type="date" aria-label="Enviada desde" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className={`rounded-lg border px-2 py-1 text-xs outline-none ${fieldCls}`} />
              <span className={`text-xs ${tc.textSecondary}`}>a</span>
              <input type="date" aria-label="Enviada hasta" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className={`rounded-lg border px-2 py-1 text-xs outline-none ${fieldCls}`} />
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Vence">
          <span className={`w-20 text-xs ${tc.textSecondary}`}>
            <i className="ri-timer-line mr-1" />
            Vence
          </span>
          {(Object.keys(EXPIRY_LABEL) as ExpiryFilter[]).map((x) => (
            <button key={x} className={chip(expiry === x)} aria-pressed={expiry === x} onClick={() => setExpiry(x)}>
              {EXPIRY_LABEL[x]}
            </button>
          ))}
          <span className={`ml-4 text-xs ${tc.textSecondary}`}>
            <i className="ri-flow-chart mr-1" />
            Etapa del negocio
          </span>
          <select aria-label="Etapa del negocio" value={stage} onChange={(e) => setStage(e.target.value)} className={`rounded-lg border px-3 py-1.5 text-xs outline-none ${fieldCls}`}>
            <option value="">Todas</option>
            {stages.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          tc={tc}
          accent
          icon="ri-scales-3-line"
          label="En negociación (ponderado)"
          value={money(kpis.weighted)}
          hint={`${money(kpis.inNegotiation)} sin ponderar`}
        />
        <Kpi tc={tc} icon="ri-send-plane-line" label="Cotizaciones enviadas" value={String(kpis.count)} hint={`${kpis.perMonth.toLocaleString("es-MX", { maximumFractionDigits: 1 })} por mes · ${kpis.months} mes(es)`} />
        <Kpi tc={tc} icon="ri-price-tag-3-line" label="Valor promedio" value={money(kpis.averageValue)} hint={`${kpis.priced} de ${kpis.count} con importe`} />
        <Kpi tc={tc} icon="ri-trophy-line" label="Ganado" value={money(kpis.buckets.ganada.total)} hint={`${kpis.buckets.ganada.count} cotización(es)`} />
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="quote-buckets">
        {([...OPEN_BUCKETS, "congelada", "perdida"] as QuoteBucket[]).map((b) => (
          <div key={b} data-testid={`quote-bucket-${b}`} className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${BUCKET_META[b].tone}`}>
                <i className={BUCKET_META[b].icon} />
                {BUCKET_META[b].label}
              </span>
              <span className={`text-[11px] ${tc.textSecondary}`}>{OPEN_BUCKETS.includes(b) ? `pesa ${Math.round(BUCKET_WEIGHT[b] * 100)} %` : "no suma"}</span>
            </div>
            <div className={`text-base font-bold ${tc.text}`}>{money(kpis.buckets[b].total)}</div>
            <div className={`text-[11px] ${tc.textSecondary}`}>{kpis.buckets[b].count} cotización(es)</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
          <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando cotizaciones...
        </div>
      ) : error ? (
        <div className="p-10 text-center">
          <p className="text-sm text-red-500">{error}</p>
          <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className={`rounded-xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
          <i className="ri-inbox-line mb-2 block text-3xl" />
          No hay cotizaciones enviadas en este periodo con estos filtros.
          {preset !== "all" && (
            <button onClick={() => setPreset("all")} className="ml-1 text-primary-500 underline">
              Ver todas
            </button>
          )}
        </div>
      ) : (
        <div className={`overflow-x-auto rounded-xl border ${tc.cardBorder} ${boardScrollbar(isDarkMode)}`}>
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="bg-primary-500 text-white">
              <tr>
                <th className="p-3 text-left font-bold">Cotización</th>
                <th className="p-3 text-left font-bold">Empresa</th>
                <th className="p-3 text-right font-bold">Importe</th>
                <th className="p-3 text-left font-bold">Enviada</th>
                <th className="p-3 text-left font-bold">Válida hasta</th>
                <th className="p-3 text-center font-bold">Temperatura</th>
                <th className="p-3 text-left font-bold">Etapa del negocio</th>
                <th className="p-3 text-center font-bold">Estado</th>
                <th className="p-3 text-center font-bold">Documento</th>
                <th className="p-3 text-left font-bold">Origen</th>
                <th className="p-3 text-center font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((quote, i) => {
                const bucket = bucketOf(quote, now);
                const left = daysLeft(quote, now);
                const hasDoc = Boolean(quote.document || quote.attachmentId || quote.documentUrl);
                return (
                  <tr
                    key={quote.id}
                    data-testid={`quote-row-${quote.id}`}
                    className={
                      isDarkMode
                        ? i % 2 === 0 ? "bg-[#232323] text-white" : "bg-[#1C1C1C] text-white"
                        : i % 2 === 0 ? "bg-gray-50 text-gray-900" : "bg-white text-gray-900"
                    }
                  >
                    <td className="p-2 font-medium">
                      {isItemized(quote) && !isMirrorQuote(quote) ? (
                        <button onClick={() => openQuote(quote)} className="text-left hover:text-primary-500 hover:underline">
                          {quote.title || quote.folio || "—"}
                        </button>
                      ) : (
                        quote.title || "—"
                      )}
                    </td>
                    <td className="p-2">
                      {quote.company || "—"}
                      {quote.contact && <div className={`text-xs ${tc.textSecondary}`}>{quote.contact}</div>}
                    </td>
                    <td className="p-2 text-right">
                      {amountOf(quote) > 0 ? (
                        <span className="font-semibold">{money(amountOf(quote), quote.currency || "MXN")}</span>
                      ) : (
                        <button onClick={() => openQuote(quote)} className="text-xs text-primary-500 underline" title="Capturar el importe">
                          Sin importe
                        </button>
                      )}
                    </td>
                    <td className="whitespace-nowrap p-2">{fmtDate(quote.sentAt ? Date.parse(quote.sentAt) : null)}</td>
                    <td className="whitespace-nowrap p-2">
                      {fmtDate(validUntilMs(quote))}
                      {left !== null && (
                        <div className={`text-xs ${left < 0 ? "text-red-500" : left <= 7 ? "text-amber-600" : tc.textSecondary}`}>
                          {left < 0 ? `venció hace ${-left} día(s)` : left === 0 ? "vence hoy" : `faltan ${left} día(s)`}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${BUCKET_META[bucket].tone}`}>
                        <i className={BUCKET_META[bucket].icon} />
                        {BUCKET_META[bucket].label}
                      </span>
                    </td>
                    <td className="p-2">{quote.dealStageLabel || "—"}</td>
                    <td className="p-2 text-center">
                      <Badge value={effectiveStatus(quote, now)} />
                    </td>
                    <td className="p-2 text-center">
                      {hasDoc ? (
                        <button
                          onClick={() => openDocument(quote)}
                          title={quote.document?.name || quote.attachmentName || "Abrir el documento"}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-500 hover:bg-primary-500/10"
                        >
                          <i className="ri-file-text-line" />
                          Ver
                        </button>
                      ) : (
                        <button onClick={() => setEditing(quote)} className={`text-xs underline ${tc.textSecondary}`} title="Adjuntar el documento">
                          Adjuntar
                        </button>
                      )}
                    </td>
                    <td className="p-2">
                      <QuoteOrigin quote={quote} />
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => openQuote(quote)}
                        aria-label={`Editar ${quote.title ?? "cotización"}`}
                        title="Editar importe, vigencia, estado y documento"
                        className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className={`mt-3 text-xs ${tc.textSecondary}`}>
        <i className="ri-information-line mr-1" />
        Una cotización se enfría durante su vigencia (30 días si no se indica otra): caliente el primer tercio, tibia el segundo y fría el
        último. Pasada la fecha «Válida hasta» queda congelada y se lee como cancelada. Si el negocio pasa a perdido o a Follow Up Futuro, su
        importe se suma en Perdida.
      </p>

      {editing && (
        <Modal
          title={`Cotización — ${editing.title ?? ""}`}
          onClose={() => setEditing(null)}
          themeClasses={tc}
          content={
            <QuoteEditor
              key={editing.id}
              quote={editing}
              tc={tc}
              fieldCls={fieldCls}
              onSaved={(patch) => {
                patchLocal(editing.id, patch);
                setEditing((prev) => (prev ? { ...prev, ...patch } : prev));
              }}
              onClose={() => setEditing(null)}
              onOpenDocument={openDocument}
            />
          }
        />
      )}
    </div>
  );
}

// --- editor -----------------------------------------------------------------

function QuoteEditor({
  quote,
  tc,
  fieldCls,
  onSaved,
  onClose,
  onOpenDocument,
}: {
  quote: Quote;
  tc: TC;
  fieldCls: string;
  onSaved: (patch: Partial<Quote>) => void;
  onClose: () => void;
  onOpenDocument: (quote: Quote) => void;
}) {
  const [amount, setAmount] = useState(amountOf(quote) ? String(amountOf(quote)) : "");
  const [validUntil, setValidUntil] = useState(toInputDate(validUntilMs(quote)));
  const [status, setStatus] = useState(String(quote.status ?? "enviada"));
  const [documentUrl, setDocumentUrl] = useState(quote.documentUrl ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ ok: false, text: errText(e, "No se pudo guardar") });
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run("save", async () => {
      const value = amount.trim() === "" ? 0 : Number(amount);
      if (!Number.isFinite(value) || value < 0) throw new Error("El importe debe ser un número mayor o igual a 0");
      const link = documentUrl.trim();
      if (link && !/^https?:\/\//i.test(link)) throw new Error("El enlace del documento debe empezar con http:// o https://");
      // The date input is a local day; the quote stands until the END of that day.
      const expiresAt = validUntil ? new Date(`${validUntil}T23:59:59`).toISOString() : null;
      const patch = { amount: value, expiresAt, status, documentUrl: link || null };
      await updateQuote(quote.id, patch);
      onSaved(patch);
      setMessage({ ok: true, text: "Cotización guardada" });
    });

  const upload = (file: File | undefined) => {
    if (!file) return;
    void run("upload", async () => {
      const document = await uploadQuoteDocument(quote.id, file);
      onSaved({ document });
      setMessage({ ok: true, text: `Documento adjuntado: ${document.name ?? file.name}` });
    });
  };

  // Use (or stop using) a file HubSpot had on the deal as this quote's document.
  const pickAttachment = (id: string | null, name: string | null) =>
    run("pick", async () => {
      const patch = { attachmentId: id, attachmentName: name };
      await updateQuote(quote.id, patch);
      onSaved(patch);
      setMessage({ ok: true, text: id ? `Documento de la cotización: ${name}` : "Se dejó de usar el adjunto" });
    });

  const removeDocument = () =>
    run("remove", async () => {
      await removeQuoteDocument(quote.id);
      onSaved({ document: null });
      setMessage({ ok: true, text: "Documento quitado" });
    });

  const label = `mb-1 block text-xs ${tc.textSecondary}`;
  const input = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${fieldCls}`;

  return (
    <div className="space-y-4" data-testid="quote-editor">
      <div className={`text-sm ${tc.textSecondary}`}>
        {quote.company || "Sin empresa"}
        {quote.dealStageLabel && ` · ${quote.dealStageLabel}`}
        {quote.sentAt && ` · enviada el ${fmtDate(Date.parse(quote.sentAt))}`}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className={label} htmlFor="quote-amount">
            Importe (MXN)
          </label>
          <input id="quote-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className={input} />
        </div>
        <div>
          <label className={label} htmlFor="quote-valid-until">
            Válida hasta
          </label>
          <input id="quote-valid-until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={input} />
        </div>
        <div>
          <label className={label} htmlFor="quote-status">
            Estado
          </label>
          <select id="quote-status" value={status} onChange={(e) => setStatus(e.target.value)} className={input}>
            {[...new Set([...STATUS_OPTIONS, status])].map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`space-y-3 border-t pt-4 ${tc.border}`}>
        <h3 className={`text-sm font-bold ${tc.text}`}>Documento</h3>
        {quote.document ? (
          <div className={`flex flex-wrap items-center gap-2 rounded-lg border p-3 ${tc.border}`}>
            <i className="ri-file-text-line text-lg text-primary-500" />
            <span className={`min-w-0 flex-1 truncate text-sm ${tc.text}`}>{quote.document.name ?? "Documento"}</span>
            <button onClick={() => onOpenDocument(quote)} className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-400">
              Ver documento
            </button>
            <button onClick={removeDocument} disabled={!!busy} className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50">
              Quitar
            </button>
          </div>
        ) : quote.attachmentId ? (
          <div className={`flex flex-wrap items-center gap-2 rounded-lg border p-3 ${tc.border}`}>
            <i className="ri-attachment-2 text-lg text-primary-500" />
            <span className={`min-w-0 flex-1 truncate text-sm ${tc.text}`}>{quote.attachmentName ?? "Adjunto de HubSpot"}</span>
            <button onClick={() => onOpenDocument(quote)} className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-400">
              Ver documento
            </button>
            <button onClick={() => pickAttachment(null, null)} disabled={!!busy} className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50">
              Dejar de usar
            </button>
          </div>
        ) : (
          <p className={`text-xs ${tc.textSecondary}`}>Esta cotización todavía no tiene documento adjunto.</p>
        )}
        {quote.dealId && (
          <div data-testid="quote-deal-attachments">
            <p className={`mb-2 text-xs ${tc.textSecondary}`}>Adjuntos de HubSpot en este negocio y su contacto:</p>
            <AttachmentsList
              dealId={quote.dealId}
              textClass={tc.text}
              mutedClass={tc.textSecondary}
              borderClass={tc.border}
              pickLabel="Usar en esta cotización"
              pickedId={quote.attachmentId}
              onPick={(a) => pickAttachment(a.id, a.name)}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            aria-label="Archivo de la cotización"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(e) => {
              upload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button onClick={() => fileInput.current?.click()} disabled={!!busy} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${fieldCls}`}>
            <i className={busy === "upload" ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} />
            {quote.document ? "Reemplazar archivo" : "Adjuntar archivo"}
          </button>
          <span className={`text-xs ${tc.textSecondary}`}>PDF, Word, Excel, PowerPoint o imagen, hasta 10 MB.</span>
        </div>
        <div>
          <label className={label} htmlFor="quote-document-url">
            O un enlace al documento (Drive, etc.)
          </label>
          <input id="quote-document-url" type="url" value={documentUrl} onChange={(e) => setDocumentUrl(e.target.value)} placeholder="https://" className={input} />
        </div>
      </div>

      {message && <p className={`text-sm ${message.ok ? "text-emerald-600" : "text-red-500"}`}>{message.text}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${fieldCls}`}>
          Cerrar
        </button>
        <button onClick={save} disabled={!!busy} className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50">
          <i className={busy === "save" ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
          Guardar
        </button>
      </div>
    </div>
  );
}
